// src/pages/ListDetail.tsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import EditIcon from "@mui/icons-material/Edit";
import PersonAddAltIcon from "@mui/icons-material/PersonAddAlt";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import { DataGrid, GridColDef, GridToolbar } from "@mui/x-data-grid";
import { useNavigate, useParams } from "react-router-dom";
import {
  arrayRemove,
  arrayUnion,
  collection,
  deleteField,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  serverTimestamp,
  Timestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { db } from "../firebase";
import PageHeader from "../components/PageHeader";

// ----- Types -----
type Role = "owner" | "editor" | "viewer" | "none";

interface ListDoc {
  name?: string;
  userId?: string;
  createdAt?: Timestamp | Date | null;
  updatedAt?: Timestamp | Date | null;
  roles?: Record<string, Role>;
  memberIds?: string[];
  itemCount?: number;
}

interface UserDirectory {
  uid: string;
  email: string;
  emailLower?: string;
  firstName?: string;
  lastName?: string;
  displayName?: string;
}

interface Snack {
  open: boolean;
  message: string;
  severity: "success" | "info" | "warning" | "error";
}

// ---------- Component ----------
export default function ListManagerDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [list, setList] = useState<ListDoc | null>(null);
  const [loading, setLoading] = useState(true);
  const [snack, setSnack] = useState<Snack>({
    open: false,
    message: "",
    severity: "success",
  });

  // Role management state
  const [roleSearchEmail, setRoleSearchEmail] = useState("");
  const [searching, setSearching] = useState(false);
  const [foundUser, setFoundUser] = useState<UserDirectory | null>(null);
  const [selectedRole, setSelectedRole] = useState<Role>("viewer");
  const [notFoundMsg, setNotFoundMsg] = useState("");

  const [memberProfiles, setMemberProfiles] = useState<
    Record<string, UserDirectory>
  >({});

  // ---- Load list ----
  useEffect(() => {
    if (!id) return;

    const ref = doc(db, "lists", id);
    const unsub = onSnapshot(
      ref,
      async (snap) => {
        if (!snap.exists()) {
          setList(null);
          setLoading(false);
          return;
        }

        const data = snap.data() as any;
        const toDate = (v: any): Date | undefined =>
          v instanceof Timestamp
            ? v.toDate()
            : v instanceof Date
            ? v
            : undefined;

        // Count items
        const itemsSnap = await getDocs(collection(db, "lists", id, "items"));
        const itemCount = itemsSnap.size;

        setList({
          ...data,
          createdAt: toDate(data.createdAt),
          updatedAt: toDate(data.updatedAt),
          itemCount,
        });
        setLoading(false);
      },
      (err) => {
        console.error("ListManagerDetail: snapshot error", err);
        setSnack({
          open: true,
          message: "Failed to load list.",
          severity: "error",
        });
        setLoading(false);
      }
    );

    return () => unsub();
  }, [id]);

  const ownerUid = list?.userId ?? "";
  const title = list?.name || "(Untitled list)";

  // ---- Role helpers ----
  const resolveDisplay = (u: UserDirectory | undefined) => {
    if (!u) return "";
    const name = (u.displayName ||
      `${u.firstName || ""} ${u.lastName || ""}`.trim()).trim();
    return name || u.email || u.uid;
  };

  // Load profiles for current members
  useEffect(() => {
    const load = async () => {
      const uids = Object.keys(list?.roles || {});
      if (uids.length === 0) {
        setMemberProfiles({});
        return;
      }
      const out: Record<string, UserDirectory> = {};
      await Promise.all(
        uids.map(async (uid) => {
          try {
            const snap = await getDoc(doc(db, "users", uid));
            if (snap.exists()) {
              const d = snap.data() as any;
              out[uid] = {
                uid,
                email: d.email || "",
                emailLower: d.emailLower || d.email?.toLowerCase?.(),
                firstName: d.firstName,
                lastName: d.lastName,
                displayName: d.displayName,
              };
            } else {
              out[uid] = { uid, email: "" };
            }
          } catch {
            out[uid] = { uid, email: "" };
          }
        })
      );
      setMemberProfiles(out);
    };
    load();
  }, [list?.roles]);

  // Search for user by email
  const searchUsers = async () => {
    const termRaw = roleSearchEmail.trim();
    const term = termRaw.toLowerCase();
    setFoundUser(null);
    setNotFoundMsg("");
    if (!term) return;

    setSearching(true);
    try {
      const qy = query(collection(db, "users"), where("emailLower", "==", term));
      const snap = await getDocs(qy);

      if (snap.empty) {
        setNotFoundMsg(`No user found for ${termRaw}`);
        return;
      }

      const d = snap.docs[0];
      const v = d.data() as any;
      const u: UserDirectory = {
        uid: d.id,
        email: v.email || "",
        emailLower: v.emailLower || v.email?.toLowerCase?.(),
        firstName: v.firstName,
        lastName: v.lastName,
        displayName: v.displayName,
      };

      // Owner special case
      if (u.uid === ownerUid) {
        setNotFoundMsg("This is your list — you already own it.");
        return;
      }

      // Already a member special case
      const existingRole = list?.roles?.[u.uid] as Role | undefined;
      if (existingRole) {
        setNotFoundMsg(`Already a member (${existingRole}).`);
        return;
      }

      setFoundUser(u);
    } catch (e) {
      console.error("ListManagerDetail: user search failed", e);
      setSnack({ open: true, message: "User search failed.", severity: "error" });
    } finally {
      setSearching(false);
    }
  };

  // Assign role (with owner/duplicate protection)
  const assignRole = async () => {
    if (!foundUser) {
      setSnack({
        open: true,
        message: "Search and select a user first.",
        severity: "warning",
      });
      return;
    }
    if (foundUser.uid === ownerUid) {
      setSnack({
        open: true,
        message: "You already own this list.",
        severity: "info",
      });
      return;
    }

    // Avoid adding a user twice
    const existingRole = list?.roles?.[foundUser.uid] as Role | undefined;
    if (existingRole) {
      setSnack({
        open: true,
        message: `Already a member (${existingRole}).`,
        severity: "info",
      });
      return;
    }

    try {
      await updateDoc(doc(db, "lists", id!), {
        [`roles.${foundUser.uid}`]: selectedRole,
        memberIds: arrayUnion(foundUser.uid),
        updatedAt: serverTimestamp(),
      });
      setSnack({ open: true, message: "Role assigned.", severity: "success" });
      setFoundUser(null);
      setRoleSearchEmail("");
      setSelectedRole("viewer");
      setNotFoundMsg("");
    } catch (e) {
      console.error("ListManagerDetail: assign role error", e);
      setSnack({
        open: true,
        message: "Failed to assign role.",
        severity: "error",
      });
    }
  };

  // Change role
  const changeRole = async (uid: string, newRole: Role) => {
    if (uid === ownerUid) return;
    const current = list?.roles?.[uid];
    if (current === newRole) return;
    try {
      await updateDoc(doc(db, "lists", id!), {
        [`roles.${uid}`]: newRole,
        updatedAt: serverTimestamp(),
      });
      setSnack({ open: true, message: "Role updated.", severity: "success" });
    } catch (e) {
      console.error("ListManagerDetail: change role error", e);
      setSnack({
        open: true,
        message: "Failed to update role.",
        severity: "error",
      });
    }
  };

  // Remove member
  const removeMember = async (uid: string) => {
    if (uid === ownerUid) return;
    if (!window.confirm("Remove this member from the list?")) return;
    try {
      await updateDoc(doc(db, "lists", id!), {
        [`roles.${uid}`]: deleteField(),
        memberIds: arrayRemove(uid),
        updatedAt: serverTimestamp(),
      });
      setSnack({ open: true, message: "Member removed.", severity: "success" });
    } catch (e) {
      console.error("ListManagerDetail: remove member error", e);
      setSnack({
        open: true,
        message: "Failed to remove member.",
        severity: "error",
      });
    }
  };

  // Member grid
  type MemberRow = { id: string; name: string; email: string; role: Role; isOwner: boolean };

  const memberRows: MemberRow[] = useMemo(() => {
    const entries = Object.entries(list?.roles || {});
    return entries.map(([uid, role]) => {
      const prof = memberProfiles[uid];
      const name = resolveDisplay(prof);
      const email = prof?.email || "";
      const isOwner = uid === ownerUid;
      return { id: uid, name, email, role: role as Role, isOwner };
    });
  }, [list?.roles, ownerUid, memberProfiles]);

  const memberCols: GridColDef<MemberRow>[] = [
    { field: "name", headerName: "User", flex: 1, minWidth: 200 },
    { field: "email", headerName: "Email", flex: 1, minWidth: 220 },
    {
      field: "role",
      headerName: "Role",
      width: 160,
      renderCell: ({ row }) =>
        row.isOwner ? (
          <span>owner</span>
        ) : (
          <Select
            size="small"
            value={row.role}
            onChange={(e) => changeRole(row.id, e.target.value as Role)}
          >
            <MenuItem value="viewer">viewer</MenuItem>
            <MenuItem value="editor">editor</MenuItem>
          </Select>
        ),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 120,
      renderCell: ({ row }) =>
        row.isOwner ? null : (
          <Tooltip title="Remove member">
            <span>
              <IconButton onClick={() => removeMember(row.id)}>
                <RemoveCircleOutlineIcon />
              </IconButton>
            </span>
          </Tooltip>
        ),
    },
  ];

  const handleEdit = () => navigate(`/lists/${id}/edit`);

  return (
    <Box sx={{ p: 2, display: "flex", flexDirection: "column", minHeight: "calc(100vh - 96px)" }}>
      <PageHeader
        onBack={() => navigate("/lists")}
        backAriaLabel="Back to lists"
        title="List Manager"
        subtitle={list?.name ? `List: ${list.name}` : undefined}
      />

      {/* Main content */}
      <Box sx={{ flex: 1 }}>
        <Box
          sx={{
            display: "grid",
            gap: 2,
            gridTemplateColumns: { xs: "1fr", md: "7fr 5fr" },
            alignItems: "start",
          }}
        >
          {/* LEFT PANEL */}
          <Box>
            <Card variant="outlined">
              <CardContent>
                <Stack spacing={2}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="h5">{loading ? "Loading..." : title}</Typography>
                    <Chip label="Always published" color="success" variant="filled" />
                  </Stack>

                  <Stack direction="row" spacing={2} flexWrap="wrap">
                    <Typography variant="body2">
                      <strong>Items:</strong> {list?.itemCount ?? 0}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Created:</strong>{" "}
                      {list?.createdAt instanceof Date ? list.createdAt.toLocaleString() : "—"}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Last updated:</strong>{" "}
                      {list?.updatedAt instanceof Date ? list.updatedAt.toLocaleString() : "—"}
                    </Typography>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          </Box>

          {/* RIGHT PANEL: Quick Actions */}
          <Box>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Quick Actions
                </Typography>
                <Stack spacing={1}>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={handleEdit}
                    startIcon={<EditIcon />}
                    disabled={!id}
                  >
                    Edit items
                  </Button>
                  <Tooltip title="Disabled to prevent accidental deletion">
                    <span>
                      <Button
                        fullWidth
                        variant="outlined"
                        color="error"
                        startIcon={<DeleteForeverIcon />}
                        disabled
                      >
                        Delete list (disabled)
                      </Button>
                    </span>
                  </Tooltip>
                </Stack>
              </CardContent>
            </Card>
          </Box>

          {/* ROLES SECTION */}
          <Box sx={{ gridColumn: "1 / -1" }}>
            <Card variant="outlined">
              <CardContent>
                <Stack spacing={2}>
                  <Typography variant="h6">List Roles</Typography>

                  {/* Assign new role */}
                  <Stack
                    direction={{ xs: "column", md: "row" }}
                    spacing={1}
                    alignItems="center"
                  >
                    <TextField
                      fullWidth
                      size="small"
                      label="Search by email"
                      placeholder="user@example.com"
                      value={roleSearchEmail}
                      onChange={(e) => setRoleSearchEmail(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") searchUsers();
                      }}
                    />
                    <Button
                      variant="outlined"
                      onClick={searchUsers}
                      disabled={!roleSearchEmail.trim() || searching}
                    >
                      Search
                    </Button>

                    <TextField
                      select
                      size="small"
                      label="Role"
                      value={selectedRole}
                      onChange={(e) => setSelectedRole(e.target.value as Role)}
                      sx={{ minWidth: 140 }}
                    >
                      <MenuItem value="viewer">viewer</MenuItem>
                      <MenuItem value="editor">editor</MenuItem>
                    </TextField>

                    <Button
                      variant="contained"
                      startIcon={<PersonAddAltIcon />}
                      onClick={assignRole}
                      disabled={!foundUser}
                    >
                      Assign
                    </Button>
                  </Stack>

                  {/* Search feedback */}
                  {notFoundMsg && (
                    <Typography variant="body2" color="text.secondary">
                      {notFoundMsg}
                    </Typography>
                  )}

                  {foundUser && (
                    <Box
                      sx={{
                        mt: 1,
                        p: 1,
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 1,
                        flexWrap: "wrap",
                      }}
                    >
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {(
                            foundUser.displayName ||
                            `${foundUser.firstName || ""} ${foundUser.lastName || ""}`.trim()
                          ) || foundUser.email}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {foundUser.email}
                        </Typography>
                      </Box>
                      <Chip label="Ready to assign" size="small" />
                    </Box>
                  )}

                  {/* Current members */}
                  <div style={{ width: "100%" }}>
                    <DataGrid
                      rows={memberRows}
                      columns={memberCols}
                      autoHeight
                      pageSizeOptions={[5, 10]}
                      initialState={{
                        pagination: { paginationModel: { pageSize: 5 } },
                      }}
                      disableRowSelectionOnClick
                      slots={{ toolbar: GridToolbar }}
                      slotProps={{
                        toolbar: {
                          showQuickFilter: true,
                          printOptions: { disableToolbarButton: true },
                        },
                      }}
                    />
                  </div>
                </Stack>
              </CardContent>
            </Card>
          </Box>
        </Box>
      </Box>

      {/* Footer */}
      <Box sx={{ mt: 3, textAlign: "center", color: "text.secondary" }}>
        <Typography variant="caption" display="block">
          Owner User ID: {ownerUid || "—"}
        </Typography>
        <Typography variant="caption" display="block">
          List ID: {id}
        </Typography>
      </Box>
      <Snackbar
        open={snack.open}
        autoHideDuration={2500}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
      >
        <Alert severity={snack.severity} variant="filled">
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
